import type { Lang } from '~/types';
import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import { MenuIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Logo from '~/assets/logo.svg';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/components/ui/sheet';
import menudata from '~/data/menu';
import { langStore } from '~/store/langStore';
import { cn } from '~/utils';
import { usePosthogEvent } from '~/utils/posthog';
import { SocialMedia } from './Footer';

// ===================================================================================
//  Types
// ===================================================================================

type MenuText = { [key in Lang]?: string };

interface SubMenuItem extends MenuText {
  to: string;
}

interface MenuItem extends MenuText {
  to: string;
  subitems?: Record<string, SubMenuItem>;
}

// ===================================================================================
//  Language Selector Components
// ===================================================================================

function LanguageButton({ language }: { language: Lang }) {
  const currentLanguage = useStore(langStore, s => s);
  const isActive = currentLanguage === language;
  const track = usePosthogEvent();

  const setNewLanguage = useCallback(() => {
    langStore.setState(() => language);
    track('locale_change', { locale: language });
  }, [language, track]);

  return (
    <button
      type="button"
      className={cn(
        'border-red text-beige3 cursor-pointer border-b-2 pb-2 transition-colors hover:text-white',
        isActive && 'border-white text-white',
      )}
      onClick={setNewLanguage}
    >
      {language}
    </button>
  );
}

export function LanguageSelector() {
  return (
    <div className="flex gap-2 text-xs lg:mt-0 lg:text-sm">
      <LanguageButton language="HU" />
      <LanguageButton language="RS" />
      <LanguageButton language="EN" />
    </div>
  );
}

// ===================================================================================
// Menu Link Component
// ===================================================================================

function MenuLink({ menu, isMobile }: { menu: MenuItem; isMobile?: boolean }) {
  const locale = useStore(langStore, s => s);
  const location = useRouterState({ select: s => s.location });
  const [isHovered, setIsHovered] = useState(false);
  const isGallery = menu.to === '/gallery';

  if (isMobile) {
    return (
      <div>
        <Link
          to={menu.to}
          className={cn(
            { 'text-red': location.pathname === menu.to && !isGallery },
            { 'text-orange font-bold': isGallery },
            'block px-4 py-2 text-sm font-semibold text-white transition-colors hover:text-white',
          )}
        >
          {menu[locale]}
        </Link>
        {menu.subitems && (
          <div className="ml-4">
            {Object.values(menu.subitems).map((subitem: SubMenuItem, subIndex) =>
              subitem[locale]
                ? (
                    <Link
                      key={`subitem-${subIndex}`}
                      to={subitem.to}
                      className="block px-4 py-2 text-sm font-semibold transition-colors hover:text-white"
                    >
                      {subitem[locale]}
                    </Link>
                  )
                : null,
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to={menu.to}
        className={cn(
          'block px-1 py-1 text-xs font-semibold transition-all duration-300 lg:px-2 lg:py-2 lg:text-sm',
          {
            'text-white': location.pathname === menu.to && !isGallery,
            'text-beige3 hover:text-white': !isGallery,
            'text-orange relative font-bold hover:text-yellow': isGallery,
            'animate-shadow-pulse': isGallery,
          },
        )}
      >
        {menu[locale]}
      </Link>
      {menu.subitems && isHovered && (
        <div className="bg-red text-beige3 absolute top-full left-0 z-10 mt-0 w-48 rounded-md shadow-md">
          {Object.values(menu.subitems).map((subitem: SubMenuItem, index) =>
            subitem[locale]
              ? (
                  <Link
                    key={`subitem-${index}`}
                    to={subitem.to}
                    className="block px-1 py-1 text-xs font-semibold transition-colors hover:text-white lg:px-2 lg:py-2 lg:text-sm"
                  >
                    {subitem[locale]}
                  </Link>
                )
              : null,
          )}
        </div>
      )}
    </div>
  );
}

// ===================================================================================
//  Navbar Component
// ===================================================================================

export default function Navbar() {
  const lang = useStore(langStore, s => s);
  // const festivalMode = useFeatureFlagPayload('festival-mode') as string[];
  const menus = Object.values(menudata.menus)
    .filter(menu => menu[lang]);
    // .filter(menu => !festivalMode || (festivalMode && festivalMode.includes(menu.to as string)));
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const router = useRouter();

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  useEffect(() => {
    router.subscribe('onBeforeNavigate', () => {
      closeSidebar();
    });
  }, [router, closeSidebar]);

  return (
    <div className="bg-red fixed top-0 z-30 w-full text-white shadow-md">
      <div className="mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" className="flex items-center">
          <img src={Logo} alt="Logo" className="h-8 fill-white" />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden items-center space-x-4 lg:flex">
          {menus.map((menu, index) => (
            <MenuLink key={`menu-${index}`} menu={menu} />
          ))}
        </div>

        <LanguageSelector />
        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet open={isSidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <button type="button" className="text-white focus:outline-none">
                <MenuIcon size={24} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-red flex w-80 flex-col text-white">
              <SheetHeader>
                <SheetTitle className="text-white">Malomfesztivál 2025</SheetTitle>
                <SheetDescription className="text-white">
                  <LanguageSelector />
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col">
                  {menus.map((menu, index) => (
                    <MenuLink key={`mobile-menu-${index}`} menu={menu} isMobile />
                  ))}
                </div>
                <SocialMedia />
              </div>
              <SheetFooter>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
}
